import { fmtElapsedSince } from "@/lib/utils"
import { usePage } from "@inertiajs/react"
import { useEffect, useRef } from "react"

interface RunningEntry {
  id: number
  project_id: number | null
  project_name: string
  customer_name: string
  description: string | null
  started_at: string | null
}

interface SharedProps {
  [key: string]: unknown
  running_entry: RunningEntry | null
}

const FAVICON_SELECTORS = 'link[rel="icon"], link[rel="apple-touch-icon"]'

function buildRunningFavicon(): string | null {
  if (typeof document === "undefined") return null
  const canvas = document.createElement("canvas")
  canvas.width = 32
  canvas.height = 32
  const ctx = canvas.getContext("2d")
  if (!ctx) return null

  ctx.fillStyle = "#0f172a"
  ctx.beginPath()
  ctx.roundRect(2, 2, 28, 28, 6)
  ctx.fill()

  ctx.fillStyle = "#ffffff"
  ctx.beginPath()
  ctx.moveTo(12, 9)
  ctx.lineTo(24, 16)
  ctx.lineTo(12, 23)
  ctx.closePath()
  ctx.fill()

  ctx.fillStyle = "#ef4444"
  ctx.beginPath()
  ctx.arc(24, 8, 6, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = "#ffffff"
  ctx.lineWidth = 1.5
  ctx.stroke()

  return canvas.toDataURL("image/png")
}

export function useTrackerTabFeedback() {
  const { props } = usePage<SharedProps>()
  const entry = props.running_entry

  const originalTitleRef = useRef<string | null>(null)
  const originalFaviconsRef = useRef<Array<{ el: HTMLLinkElement; href: string }>>([])
  const runningFaviconRef = useRef<string | null>(null)

  useEffect(() => {
    if (!entry?.started_at) return
    const startedAt = entry.started_at
    const label = entry.description?.trim() || entry.project_name || "Tracking"

    if (originalTitleRef.current === null) {
      originalTitleRef.current = document.title
    }
    if (originalFaviconsRef.current.length === 0) {
      const links = document.querySelectorAll<HTMLLinkElement>(FAVICON_SELECTORS)
      originalFaviconsRef.current = Array.from(links).map((el) => ({ el, href: el.href }))
    }
    if (runningFaviconRef.current === null) {
      runningFaviconRef.current = buildRunningFavicon()
    }

    let tick = 0

    function update() {
      const elapsed = fmtElapsedSince(startedAt)
      const suffix = Math.floor(tick / 3) % 2 === 0 ? label : "Tracking…"
      document.title = `⏱ ${elapsed} · ${suffix}`
      tick++
    }

    update()
    const interval = setInterval(update, 1000)

    if (runningFaviconRef.current) {
      for (const { el } of originalFaviconsRef.current) {
        el.href = runningFaviconRef.current
        el.type = "image/png"
      }
    }

    return () => {
      clearInterval(interval)
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current
        originalTitleRef.current = null
      }
      for (const { el, href } of originalFaviconsRef.current) {
        el.href = href
      }
      originalFaviconsRef.current = []
    }
  }, [entry?.started_at, entry?.description, entry?.project_name])
}
